import type { HsDisposable } from './disposable.interface';
import { HsFormatRegistry } from './format-registry.class';
import type { HsViewerHandle } from './viewer-handle.interface';
import type { HsViewerPlugin, HsViewerPluginContext } from './viewer-plugin.interface';

interface InstalledPlugin {
  plugin: HsViewerPlugin;
  disposable: HsDisposable | null;
}

/** Installs plugins into one viewer and removes them in reverse order. */
export class HsPluginHost implements HsDisposable {
  public readonly formats: HsFormatRegistry = new HsFormatRegistry();
  private readonly installed: InstalledPlugin[] = [];

  /** `viewer` is passed to every plugin's `install`. */
  public constructor(private readonly viewer?: HsViewerHandle) {}

  public use<T extends HsViewerPlugin>(plugin: T): T {
    if (this.has(plugin.name)) {
      throw new Error(`A plugin named "${plugin.name}" is already installed`);
    }
    const context: HsViewerPluginContext =
      this.viewer === undefined ? { formats: this.formats } : { formats: this.formats, viewer: this.viewer };
    const disposable: HsDisposable | void = plugin.install(context);
    this.installed.push({ plugin, disposable: disposable ?? null });
    return plugin;
  }

  public has(name: string): boolean {
    return this.installed.some((entry: InstalledPlugin) => entry.plugin.name === name);
  }

  public get<T extends HsViewerPlugin>(name: string): T | null {
    return (this.installed.find((entry: InstalledPlugin) => entry.plugin.name === name)?.plugin as T) ?? null;
  }

  public remove(name: string): boolean {
    const index: number = this.installed.findIndex((entry: InstalledPlugin) => entry.plugin.name === name);
    if (index < 0) {
      return false;
    }
    const [entry] = this.installed.splice(index, 1);
    entry.disposable?.dispose();
    return true;
  }

  public dispose(): void {
    while (this.installed.length > 0) {
      this.installed.pop()?.disposable?.dispose();
    }
  }
}
